import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';

const PAGE_SIZE = 25;
const DEFAULT_COLOR = '#3f51b5';
const EMPTY_FORM = { categoryName: '', categoryColor: DEFAULT_COLOR };

// Same accumulating "load more" fetch pattern as useProductsPage/useInventoryPage.
const useCategoriesPage = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const [searchParams] = useSearchParams();
  const previewState = searchParams.get('previewState');

  const [companyId] = useState(() => localStorage.getItem('companyId') || '');

  const [rows, setRows] = useState([]);
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('create');
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const fetchCategories = useCallback((reset = true) => {
    if (!config?.backendUrl || !companyId) return;
    if (reset) setStatus('loading'); else setLoadingMore(true);
    const page = reset ? 1 : Math.floor(rowsRef.current.length / PAGE_SIZE) + 1;
    const params = { companyId, page, pageSize: PAGE_SIZE };
    if (search) params.search = search;
    axios.get(`${config.backendUrl}/categories`, { params })
      .then((res) => {
        setRows((prev) => (reset ? res.data.items : [...prev, ...res.data.items]));
        setTotal(res.data.total);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching categories', error);
        if (error.response?.status === 403) {
          setStatus('permission');
        } else {
          setErrorInfo({
            request: `GET /categories · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}`,
          });
          setStatus('error');
        }
      })
      .finally(() => setLoadingMore(false));
  }, [config, companyId, search, t]);

  useEffect(() => { fetchCategories(true); }, [fetchCategories]);

  const loadMore = () => fetchCategories(false);
  const remaining = Math.max(0, total - rows.length);
  const nextBatchSize = Math.min(PAGE_SIZE, remaining);

  // ---- Create / edit drawer -------------------------------------------- //
  const openCreateDrawer = () => {
    setDrawerMode('create');
    setForm(EMPTY_FORM);
    setFormErrors({});
    setEditingId(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (category) => {
    setDrawerMode('edit');
    setForm({ categoryName: category.categoryName, categoryColor: category.categoryColor || DEFAULT_COLOR });
    setFormErrors({});
    setEditingId(category._id);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
  };

  const handleSave = async () => {
    if (saving) return;
    if (!form.categoryName.trim()) {
      setFormErrors({ categoryName: t('Category name is required') });
      return;
    }
    setFormErrors({});
    setSaving(true);
    try {
      const payload = { categoryName: form.categoryName, categoryColor: form.categoryColor, companyId };
      if (drawerMode === 'create') {
        await axios.post(`${config.backendUrl}/categories`, payload);
        notify(t('Category created successfully'));
      } else {
        await axios.put(`${config.backendUrl}/categories/${editingId}`, payload);
        notify(t('Category updated successfully'));
      }
      setDrawerOpen(false);
      fetchCategories();
    } catch (error) {
      console.error(error);
      notify(drawerMode === 'create' ? t('Failed to create category') : t('Failed to update category'), 'error');
    } finally {
      setSaving(false);
    }
  };

  // ---- Delete confirm ---------------------------------------------------//
  const openDeleteConfirm = (category) => {
    setConfirmTarget(category);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${config.backendUrl}/categories/${confirmTarget._id}`);
      notify(t('Category deleted successfully'));
      setConfirmOpen(false);
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category', error);
      // 409 = products were added since the list loaded; the server's message says how many.
      const inUse = error?.response?.status === 409 && error.response.data?.error;
      notify(inUse || t('Failed to delete category'), 'error');
      if (inUse) fetchCategories();
    } finally {
      setDeleting(false);
    }
  };

  const isPreviewEmptyKind = previewState === 'empty' || previewState === 'no-match';
  const effectiveStatus = isPreviewEmptyKind ? 'ready' : (previewState || status);
  const effectiveRows = isPreviewEmptyKind ? [] : (effectiveStatus === 'ready' ? rows : []);
  const isNoMatch = isPreviewEmptyKind ? previewState === 'no-match' : Boolean(search);

  return {
    t, companyId, total, errorInfo, effectiveStatus, effectiveRows, isNoMatch, search,
    searchInput, setSearchInput, setSearch,
    loadingMore, loadMore, remaining, nextBatchSize,
    drawerOpen, drawerMode, form, setForm, formErrors, saving, closeDrawer, openCreateDrawer, openEditDrawer, handleSave,
    confirmOpen, setConfirmOpen, confirmTarget, deleting, openDeleteConfirm, handleConfirmDelete,
    snackbar, setSnackbar, fetchCategories,
  };
};

export default useCategoriesPage;
